import { getAnalytics, isSupported, logEvent, type Analytics } from "firebase/analytics";
import { firebaseApp } from "./firebase";
import { demoMode } from "./mode";
import type { PaymentAnalytics } from "@juicytraffic/contracts";

let analyticsPromise: Promise<Analytics | null> | null = null;

function getFirebaseAnalytics() {
  if (
    !firebaseApp ||
    demoMode ||
    !import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
  ) {
    return Promise.resolve(null);
  }

  const app = firebaseApp;
  analyticsPromise ??= isSupported()
    .then((supported) => (supported ? getAnalytics(app) : null))
    // Analytics is optional: unsupported browsers and content blockers must not
    // prevent authentication or the dashboard from loading.
    .catch(() => null);

  return analyticsPromise;
}

export function initializeAnalytics() {
  void getFirebaseAnalytics();
}

export function trackPageView(path: string) {
  void getFirebaseAnalytics().then((analytics) => {
    if (!analytics) return;
    logEvent(analytics, "page_view", {
      page_location: window.location.href,
      page_path: path,
      page_title: document.title,
    });
  });
}

export async function trackSignUp(method: string) {
  const analytics = await getFirebaseAnalytics();
  if (!analytics) return;
  logEvent(analytics, "sign_up", { method });
}

type GtagGet = (
  command: "get",
  measurementId: string,
  field: "client_id" | "session_id",
  callback: (value: unknown) => void,
) => void;

function analyticsValue(gtag: GtagGet, field: "client_id" | "session_id") {
  return new Promise<string | undefined>((resolve) => {
    let settled = false;
    const finish = (value?: string) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    window.setTimeout(() => finish(), 1_000);
    gtag("get", import.meta.env.VITE_FIREBASE_MEASUREMENT_ID, field, (value) => {
      const text = typeof value === "string" || typeof value === "number" ? String(value) : "";
      finish(text || undefined);
    });
  });
}

/** Capture the current GA4 browser/session identity before hosted checkout. */
export async function getPaymentAnalytics(): Promise<PaymentAnalytics | undefined> {
  if (!(await getFirebaseAnalytics())) return undefined;
  const gtag = (window as typeof window & { gtag?: GtagGet }).gtag;
  if (!gtag) return undefined;
  const [clientId, sessionId] = await Promise.all([
    analyticsValue(gtag, "client_id"),
    analyticsValue(gtag, "session_id"),
  ]);
  if (!clientId) return undefined;
  return { client_id: clientId, ...(sessionId ? { session_id: sessionId } : {}) };
}
